import { useState, useEffect } from 'react';
import type Facility from '../models/facility';
import { getAllFacilitiesForAdminOrAuthorEmail } from '../services/facilities.service';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import { dataCache, FACILITIES_CACHE_KEY } from '../../../core/utils/data-cache';

interface FacilitiesState {
  readonly initialized: boolean;
  readonly facilities?: Facility[];
  readonly error?: boolean;
}

/**
 * Returns all facilities accessible to the current user.
 *
 * Performance optimisation: the response is cached in memory so that
 * navigating back to "My Offices" renders facilities instantly without a
 * visible spinner.  A background fetch always runs on mount to revalidate the
 * cache.  The cache is invalidated whenever a facility is created, updated or
 * deleted (see `facilities.service.ts`).
 *
 * @param version - Increment this value to force an explicit cache flush and
 *   re-fetch (e.g. when the admin presses "Refresh data").  Defaults to 0.
 */
const useFacilities = (version = 0): FacilitiesState => {
  // Serve cached data immediately to avoid the spinner on revisit.
  const [state, setState] = useState<FacilitiesState>(() => {
    const cached = dataCache.get<Facility[]>(FACILITIES_CACHE_KEY);
    return cached != null
      ? { initialized: true, facilities: cached }
      : { initialized: false };
  });

  const keycloak = useKeycloak();

  useEffect(() => {
    let cancelled = false;

    // When an explicit refresh is requested (version > 0), show loading state.
    if (version > 0) {
      setState({ initialized: false });
    }

    const getFacilities = async (): Promise<void> => {
      try {
        const facilities = (await getAllFacilitiesForAdminOrAuthorEmail(keycloak)) ?? [];
        if (!cancelled) {
          dataCache.set(FACILITIES_CACHE_KEY, facilities);
          setState({ initialized: true, facilities });
        }
      } catch (error) {
        if (!cancelled && !dataCache.get(FACILITIES_CACHE_KEY)) {
          setState({ initialized: false, error: true });
        }
      }
    };

    // Always revalidate in the background.
    getFacilities();

    return (): void => {
      cancelled = true;
    };
  }, [keycloak, version]);

  return state;
};

export default useFacilities;
